import React, { useState } from "react";

import LinkIcon from "@uui/Link03";
import { map } from "lodash/fp";
import { useNavigate, useSearchParams } from "react-router-dom";

import useRssLinksSettings from "@app/analytics/hooks/settings/useRssLinksSettings";
import {
  CALENDAR_PROVIDERS_BY_ID,
  RSS_CALENDAR_PROVIDERS,
} from "@app/common/constants/providers";
import { toast } from "@app/common/hooks/useToasts";
import ROUTES from "@app/common/routes";
import type { CalendarProviderId } from "@app/common/types/providerTypes";
import { isResponseErrorWithStatus } from "@app/common/utils";
import { formattedListWithConjunction } from "@app/common/utils";

import Button from "@rui/Button";
import {
  EmptyStateContainer,
  EmptyStateIcon,
  EmptyStateSubtext,
  EmptyStateTitle,
} from "@rui/EmptyStateContainer";
import { Link } from "@rui/Link";
import Stack from "@rui/Stack";
import TooltipWrapper from "@rui/TooltipWrapper";

import ConfigureRSSLinksModal from "./ConfigureRSSLinksModal";

const SchedulingEmptyState = () => {
  const navigate = useNavigate();
  const [queryParams] = useSearchParams();

  const [configureModalOpen, setConfigureModalOpen] = useState(
    queryParams.get("configuring") === "true"
  );

  const {
    hasConnectedRssCalendarProviders,
    rssCalendar,
    updateRssLinkSettingsSync,
  } = useRssLinksSettings();

  const rssCalendarProvidersLabel = formattedListWithConjunction(
    map("displayName", RSS_CALENDAR_PROVIDERS),
    "or"
  );

  const handleCloseConfigureModal = () => {
    setConfigureModalOpen(false);
    navigate(ROUTES.CALENDAR_SCHEDULING, { replace: true });
  };

  const handleEnable = () => {
    if (!rssCalendar) {
      setConfigureModalOpen(true);
      return;
    }

    updateRssLinkSettingsSync.mutate(
      { isEnabled: true },
      {
        onError: async (error) => {
          if (isResponseErrorWithStatus(error, 403)) {
            const json = await error.response.json();
            const calendarProviderDisplayName =
              CALENDAR_PROVIDERS_BY_ID[
                json.detail?.calendar_provider as CalendarProviderId
              ]?.displayName;

            toast.error({
              type: "system",
              title: "Authorization needed",
              message: `Your ${
                calendarProviderDisplayName ?? "calendar"
              } requires authorization to enable Smart Scheduler links.`,
              action: (
                <Link
                  to={ROUTES.USER_SETTINGS_INTEGRATIONS}
                  sx={{ pb: 0.5, color: "error.main" }}
                >
                  Go to Calendar settings
                </Link>
              ),
            });
          } else {
            toast.error({
              type: "system",
              title: "Update failed",
              message:
                "Something went wrong and your links could not be enabled. Please refresh the page and try again.",
            });
          }
        },
      }
    );
  };

  return (
    <>
      <Stack flex={1} padding={2}>
        <EmptyStateContainer sx={{ height: "100%" }}>
          <EmptyStateIcon Icon={LinkIcon} padding={2} />
          <EmptyStateTitle>Smart Scheduler Links</EmptyStateTitle>
          <EmptyStateSubtext>
            Eliminate the back-and-forth, and simplify scheduling meetings with
            a single link. Enable your Smart Scheduler link to get started!
          </EmptyStateSubtext>
          <TooltipWrapper
            show={!hasConnectedRssCalendarProviders}
            title={`Smart Scheduler Links requires a ${rssCalendarProvidersLabel} connection`}
          >
            <Button
              onClick={handleEnable}
              loading={updateRssLinkSettingsSync.isPending}
              disabled={!hasConnectedRssCalendarProviders}
            >
              Enable
            </Button>
          </TooltipWrapper>
        </EmptyStateContainer>
      </Stack>

      <ConfigureRSSLinksModal
        open={configureModalOpen}
        onClose={handleCloseConfigureModal}
      />
    </>
  );
};

export default SchedulingEmptyState;
